/* SSL Analyzer — vanilla JS SSE consumer.
 * State shape mirrors lib/useAnalyzerStream.ts in the SSL Web V3 repo
 * (qualityCheck, signalsCore, signalsExtended, derivations, segment,
 * recommendation, stagesCompleted, status). The Liquid section reads
 * the dispatched 'ssl-analyzer:state' event detail to render UI.
 */
(function () {
  const ENDPOINT = window.SSL_ANALYZER_ENDPOINT || 'https://analyzer-api.summerskinlab.com/api/analyze';

  const initial = {
    status: 'idle',         // idle | capturing | uploading | streaming | done | error
    photos: { front: null, left: null, right: null },
    qualityCheck: null,
    signalsCore: null,
    signalsExtended: null,
    derivations: null,
    segment: null,
    recommendation: null,
    stagesCompleted: [],
    sessionId: null,
    recordId: null,
    error: null,
  };

  let state = structuredClone(initial);
  function set(patch) {
    state = { ...state, ...patch };
    document.dispatchEvent(new CustomEvent('ssl-analyzer:state', { detail: structuredClone(state) }));
  }

  async function streamScan({ skinType, sensitivity, ageRange }) {
    set({ status: 'uploading' });
    /* Worker schema (Zod) expects flat *DataUrl keys + selfReportedSkinType
     * + sensitivityLevel with values none|mild|moderate|high. We translate
     * the UI's casual values here so the form copy stays readable. */
    const body = JSON.stringify({
      frontImageDataUrl: state.photos.front,
      leftImageDataUrl:  state.photos.left,
      rightImageDataUrl: state.photos.right,
      selfReportedSkinType: skinType,
      sensitivityLevel: sensitivity,
      ageRange,
      source: 'shopify',
      /* Locale hint for the analyzer worker — instructs the LLM to respond
         in this language. The worker reads `locale` and inserts a system-
         prompt directive (e.g. "Respond in Khmer (km-KH)"). */
      locale: (window.SSL_LOCALE || 'en').toLowerCase(),
      /* Anonymous visitor identifier generated by snippets/ssl-visitor-id.liquid.
         Mirrors to Airtable so the order webhook can link this scan to
         a subsequent purchase by the same visitor. */
      visitorId: window.SSL_VISITOR_ID || '',
      /* Explicit AI-training consent (analyzer pre-capture overlay checkbox).
         Persisted to Airtable so the training pipeline only ingests scans
         where the visitor agreed. Reads from localStorage written by the
         overlay checkbox handler. */
      aiTrainingConsent: (function () {
        try { return window.localStorage.getItem('ssl-ax-ai-training-consent-v1') === '1'; }
        catch (e) { return false; }
      })(),
    });
    let res;
    try {
      res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
        body,
      });
    } catch (e) {
      set({ status: 'error', error: 'Network error: ' + e.message });
      return;
    }
    if (!res.ok || !res.body) {
      set({ status: 'error', error: `Server error: ${res.status}` });
      return;
    }
    set({ status: 'streaming' });

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split('\n\n');
      buffer = events.pop();
      for (const evt of events) {
        const lines = evt.split('\n').map(l => l.trim()).filter(Boolean);
        const event = lines.find(l => l.startsWith('event:'))?.slice(6).trim();
        const data = lines.filter(l => l.startsWith('data:')).map(l => l.slice(5).trim()).join('\n');
        if (!data) continue;
        let parsed;
        try { parsed = JSON.parse(data); } catch { continue; }
        applyEvent(event, parsed);
      }
    }
    if (state.status !== 'error') set({ status: 'done' });
  }

  /* Stage name + field shape adapter.
   * Worker SSE shape: event: stage_complete, data: { stage, payload }
   *   stage ∈ quality_check | signals_core | signals_extended | derivations | segment | recommendation
   * Internal state shape (kept for backward compat with React reference):
   *   qualityCheck.passed (bool) ← payload.pass
   *   signalsCore.topConcerns    ← payload.top_concerns
   *   recommendation.items       ← payload.items / products (whichever the worker emits)
   * This is a minimal adapter — full schema reconciliation happens in the
   * post-design analyzer audit pass.
   */
  const STAGE_TO_KEY = {
    quality_check: 'qualityCheck',
    signals_core: 'signalsCore',
    signals_extended: 'signalsExtended',
    derivations: 'derivations',
    segment: 'segment',
    recommendation: 'recommendation',
  };
  const STAGE_TO_TAG = {
    quality_check: 'quality-check',
    signals_core: 'signals-core',
    signals_extended: 'signals-extended',
    derivations: 'derivations',
    segment: 'segment',
    recommendation: 'recommendation',
  };

  function normalizePayload(stage, p) {
    if (!p) return p;
    if (stage === 'quality_check') {
      return { passed: p.pass, issues: p.issues || [], viewMetadata: p.view_metadata };
    }
    if (stage === 'signals_core') {
      return {
        ...p,
        topConcerns: p.top_concerns || p.topConcerns || [],
        strengths: p.strengths || [],
        confidenceScore: p.confidence_score,
      };
    }
    if (stage === 'recommendation') {
      /* Worker emits { recommendation: {...full routine engine output...} }.
       * Unwrap so state.recommendation is the routine object directly, with
       * a few aliases (headline / summary) for readability in the renderer. */
      var r = p.recommendation || p;
      return {
        ...r,
        headline: r.routineHeadline || r.headline,
        summary: r.routineSummary || r.summary,
      };
    }
    return p;
  }

  function applyEvent(event, data) {
    if (event === 'scan_started') {
      /* Worker emits { sessionId, recordId? } here. Capture both for
       * Airtable cross-ref + analytics. */
      set({
        status: 'streaming',
        sessionId: (data && data.sessionId) || null,
        recordId:  (data && data.recordId)  || null,
      });
      return;
    }
    if (event === 'stage_complete' && data && data.stage) {
      const stateKey = STAGE_TO_KEY[data.stage];
      const tag = STAGE_TO_TAG[data.stage];
      if (!stateKey) return;
      const patch = {};
      patch[stateKey] = normalizePayload(data.stage, data.payload);
      patch.stagesCompleted = state.stagesCompleted.includes(tag)
        ? state.stagesCompleted
        : [...state.stagesCompleted, tag];
      set(patch);
      return;
    }
    if (event === 'scan_finalized') {
      /* Worker emits this once the Airtable record is committed. Confirms
       * recordId and any final session metadata. */
      set({
        sessionId: (data && data.sessionId) || state.sessionId,
        recordId:  (data && data.recordId)  || state.recordId,
      });
      return;
    }
    if (event === 'error') {
      set({ status: 'error', error: (data && data.message) || 'Unknown error' });
    }
  }

  // --- Result rendering -----------------------------------------------------

  /* Rich result renderer (Vision D — phase 1).
   *
   * Hero block: 8 score cards + front photo + confidence pill + routine
   * label + LLM-written lede. Zone heatmap from signalsExtended.
   * Narrative cards from recommendation.response.{en|km} (locale-aware).
   *
   * Each block self-hides when its data is absent so the card stays elegant
   * on partial responses.
   */
  function renderResult(state) {
    const card = document.querySelector('[data-ssl-result-card]');
    if (!card || !state.recommendation) return;

    const rec = state.recommendation;
    /* Locale: prefer Shopify's request locale exposed on the section root,
     * fall back to <html lang>, fall back to en. */
    const root = document.querySelector('.ssl-ax');
    const locale =
      (root && root.getAttribute('data-locale')) ||
      (document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
    const response =
      (rec.response && (rec.response[locale] || rec.response.en)) || {};
    const sig = state.signalsCore || {};
    const seg = state.segment || {};
    const ext = state.signalsExtended || null;
    /* derived.confidence_score is the V2.1 reconciliation — preferred over
     * signalsCore.confidence_score (raw LLM number) for display. */
    const derived = (rec && rec.derived) || (state.derivations && state.derivations.derived) || {};
    const confidencePct =
      (typeof derived.confidence_score === 'number' && derived.confidence_score) ||
      (typeof sig.confidenceScore === 'number' && sig.confidenceScore) ||
      null;

    /* ── Hero: routine label + LLM-written lede ───────────────────── */
    setText(card, '[data-ssl-result-headline]', rec.headline || 'Your skin profile');
    /* LLM option (i): prefer signalsCore.summary as the personalized lede.
     * Fall back to response.skinRead (templated) if summary is empty. */
    setText(card, '[data-ssl-result-lede]', sig.summary || response.skinRead || rec.summary || '');
    setText(card, '[data-ssl-result-segment]', humanizeSegment(seg, rec));
    setText(card, '[data-ssl-result-skinread]', response.skinRead || sig.summary || '');

    /* ── Hero: 8 score cards (Redness/Texture/Dryness/Uneven · Under-eye/Pores/Oiliness/Blemishes) ── */
    renderScoreCards(card, sig.signals || {});

    /* ── Hero: confidence pill ─────────────────────────────────────── */
    const confBlock = card.querySelector('[data-ssl-confidence-block]');
    if (confBlock) {
      if (typeof confidencePct === 'number') {
        confBlock.style.display = '';
        /* Cap displayed confidence at 98% — cosmetic guidance, never
         * imply absolute certainty. */
        const display = Math.min(98, Math.round(confidencePct));
        setText(card, '[data-ssl-confidence-value]', display + '%');
      } else {
        confBlock.style.display = 'none';
      }
    }

    /* ── Hero: front photo (mirror of the analyzing scan-card image) ── */
    const heroImg = card.querySelector('[data-ssl-hero-img]');
    if (heroImg && state.photos && state.photos.front) {
      heroImg.src = state.photos.front;
      /* Phase 2 — kick off face-mesh detection. Runs async; canvases fade
       * in via CSS once the parent <figure> flips to data-mesh="done". */
      applyFaceMesh(card, heroImg, sig.signals || {}, state.signalsExtended || {});
    }

    /* ── Zone heatmap from signalsExtended (8 named regions) ──────── */
    renderZones(card, ext);

    /* ── Narrative blocks ──────────────────────────────────────────── */
    setSection(card, '[data-ssl-result-priority-block]', '[data-ssl-result-priority]', response.currentPriority);
    setSection(card, '[data-ssl-result-why-block]',       '[data-ssl-result-why]',       response.whyThisRoutine);
    setSection(card, '[data-ssl-result-whynotmore-block]','[data-ssl-result-whynotmore]',response.whyNotMoreYet);
    setSection(card, '[data-ssl-result-nextstep-block]',  '[data-ssl-result-nextstep]',  response.nextStep);

    /* Climate note — only render if present */
    const climate =
      response.climateNote ||
      (rec.copy && Array.isArray(rec.copy.climateNotes)
        ? rec.copy.climateNotes.join(' ')
        : (rec.copy && rec.copy.climateNotes) || null);
    setSection(card, '[data-ssl-result-climate-block]', '[data-ssl-result-climate]', climate);

    /* Concern + strength chips */
    renderChips(card, '[data-ssl-result-signals]',   sig.topConcerns || [], formatConcern);
    renderChips(card, '[data-ssl-result-strengths]', sig.strengths   || [], s => s);

    /* Notes (LLM observations) — up to 5 */
    renderNotes(card, sig.notes || []);

    /* Daily ritual — image + text + view product link per step.
     * The previous separate "Recommended for you" section was removed;
     * its product images now live inside the ritual steps directly. */
    renderRoutineSteps(card, '[data-ssl-result-am]', rec.morning || []);
    renderRoutineSteps(card, '[data-ssl-result-pm]', rec.night   || []);
    const weekly = rec.weeklyAddOn ? [rec.weeklyAddOn] : [];
    renderRoutineSteps(card, '[data-ssl-result-weekly]', weekly);
    const weeklyBlock = card.querySelector('[data-ssl-result-weekly-block]');
    if (weeklyBlock) weeklyBlock.hidden = !weekly.length;

    /* Final encouragement line */
    setText(card, '[data-ssl-result-cta]', response.cta || '');

    /* Session footer (sessionId / recordId for cross-ref + analytics) */
    const sessionBlock = card.querySelector('[data-ssl-result-session-block]');
    if (sessionBlock && state.sessionId) {
      sessionBlock.hidden = false;
      const idEl = card.querySelector('[data-ssl-result-session-id]');
      if (idEl) idEl.textContent = String(state.sessionId).slice(0, 8) + '…';
    }
  }

  /* ── Score cards ────────────────────────────────────────────────── */

  /* Map: { worker key → { label, side } } in the order the screenshot
   * dictates — left col stacks top-to-bottom, right col stacks t-to-b. */
  const SCORE_CARDS_LEFT = [
    { key: 'redness_score',         label: 'Redness'      },
    { key: 'texture_score',         label: 'Skin texture' },
    { key: 'dryness_score',         label: 'Dryness'      },
    { key: 'tone_unevenness_score', label: 'Uneven tone'  },
  ];
  const SCORE_CARDS_RIGHT = [
    { key: 'undereye_score',        label: 'Under-eye darkness' },
    { key: 'pore_visibility_score', label: 'Pore visibility'    },
    { key: 'oiliness_score',        label: 'Oiliness'           },
    { key: 'blemish_score',         label: 'Blemishes'          },
  ];

  function renderScoreCards(card, signals) {
    const left  = card.querySelector('[data-ssl-scores-left]');
    const right = card.querySelector('[data-ssl-scores-right]');
    if (left)  renderScoreCol(left,  SCORE_CARDS_LEFT,  signals);
    if (right) renderScoreCol(right, SCORE_CARDS_RIGHT, signals);
  }
  function renderScoreCol(el, defs, signals) {
    el.innerHTML = '';
    defs.forEach(def => {
      const v = clampScore(signals[def.key]);
      const li = document.createElement('li');
      li.className = 'ssl-result__score';
      li.dataset.state = scoreState(v);
      li.innerHTML = `
        <span class="ssl-result__score-dot" aria-hidden="true"></span>
        <p class="ssl-result__score-label">${escapeHtml(def.label)}</p>
        <p class="ssl-result__score-value"><span>${v}</span><em>%</em></p>
        <span class="ssl-result__score-bar"><span style="width: ${v}%"></span></span>
      `;
      el.appendChild(li);
    });
  }
  function clampScore(v) {
    if (typeof v !== 'number' || isNaN(v)) return 0;
    return Math.max(0, Math.min(100, Math.round(v)));
  }
  function scoreState(v) {
    if (v >= 55) return 'high';
    if (v >= 30) return 'mid';
    return 'low';
  }

  /* ── Zone heatmap (signalsExtended → 8 named zones) ─────────────── */

  /* Each zone aggregates the relevant sub-scores into a single 0–1
   * intensity used for the CSS --i variable. */
  const ZONE_RECIPES = {
    forehead:     ['blemish_forehead', 'fine_lines_forehead'],
    tzone:        ['oiliness_tzone'],
    cheeks:       ['oiliness_uzone', 'dryness_cheeks', 'pore_density_cheeks'],
    periorbital:  ['undereye_darkness', 'undereye_puffiness', 'fine_lines_periorbital'],
    perioral:     ['dryness_perioral'],
    nose:         ['pore_density_nose'],
    jawline:      ['blemish_jawline'],
    pigmentation: ['pigmentation_post_blemish', 'pigmentation_sun_pattern', 'pigmentation_overall_evenness'],
  };
  /* Top sub-score driver labels for the detail line under each zone name. */
  const SUB_SCORE_LABELS = {
    blemish_forehead:               'forehead blemishes',
    fine_lines_forehead:            'forehead lines',
    oiliness_tzone:                 'oiliness',
    oiliness_uzone:                 'cheek oiliness',
    dryness_cheeks:                 'cheek dryness',
    pore_density_cheeks:            'cheek pores',
    undereye_darkness:              'darkness',
    undereye_puffiness:             'puffiness',
    fine_lines_periorbital:         'fine lines',
    dryness_perioral:               'dryness',
    pore_density_nose:              'pore density',
    blemish_jawline:                'jawline blemishes',
    pigmentation_post_blemish:      'post-blemish marks',
    pigmentation_sun_pattern:       'sun pattern',
    pigmentation_overall_evenness:  'evenness',
  };
  function renderZones(card, ext) {
    const block = card.querySelector('[data-ssl-zones-block]');
    if (!block) return;
    if (!ext) { block.hidden = true; return; }
    block.hidden = false;
    const zones = card.querySelectorAll('.ssl-result__zone');
    zones.forEach(z => {
      const name = z.dataset.zone;
      const keys = ZONE_RECIPES[name] || [];
      let avg = 0, top = null, topVal = -1;
      keys.forEach(k => {
        const v = clampScore(ext[k]);
        avg += v;
        if (v > topVal) { topVal = v; top = k; }
      });
      const intensity = keys.length ? avg / keys.length / 100 : 0;
      z.style.setProperty('--i', intensity.toFixed(3));
      const detail = z.querySelector('[data-ssl-zone-detail]');
      if (detail) {
        if (top && topVal > 0) {
          detail.textContent = SUB_SCORE_LABELS[top] + ' · ' + topVal + '%';
        } else {
          detail.textContent = '—';
        }
      }
    });
  }

  /* ── LLM notes ──────────────────────────────────────────────────── */
  function renderNotes(card, notes) {
    const block = card.querySelector('[data-ssl-result-notes-block]');
    const list  = card.querySelector('[data-ssl-result-notes]');
    if (!block || !list) return;
    list.innerHTML = '';
    if (!notes || !notes.length) { block.hidden = true; return; }
    block.hidden = false;
    notes.forEach(n => {
      const li = document.createElement('li');
      li.textContent = String(n);
      list.appendChild(li);
    });
  }

  function setText(card, selector, value) {
    const el = card.querySelector(selector);
    if (el) el.textContent = value || '';
  }

  function setSection(card, blockSelector, contentSelector, value) {
    const block = card.querySelector(blockSelector);
    if (!block) return;
    if (!value) { block.hidden = true; return; }
    block.hidden = false;
    setText(card, contentSelector, value);
  }

  function renderChips(card, selector, items, labelFn) {
    const el = card.querySelector(selector);
    if (!el) return;
    el.innerHTML = '';
    items.forEach(item => {
      const li = document.createElement('li');
      li.textContent = labelFn(item);
      el.appendChild(li);
    });
  }

  /* Step row: 88px product image + (stage label, name, reason, view link).
   * Switched to a real <img> element (was CSS background-image) so the
   * box always renders and any load failures are diagnosable in DevTools.
   * Image preference order:
   *   1. Shopify featured_image (fetched async, matches store product card)
   *   2. Worker product.imageUrl (fallback)
   *   3. Cream placeholder (always visible underneath) */
  function renderRoutineSteps(card, selector, steps) {
    const el = card.querySelector(selector);
    if (!el) return;
    el.innerHTML = '';
    steps.forEach(step => {
      if (!step || !step.product) return;
      const p = step.product;
      const li = document.createElement('li');
      li.className = 'ssl-result__step';
      const url = productUrl(p);
      const linkHtml = url
        ? `<a class="ssl-result__step-link" href="${escapeHtml(url)}">View product →</a>`
        : '';
      const initialSrc = p.imageUrl || '';
      li.innerHTML = `
        <div class="ssl-result__step-img" aria-hidden="true">
          <img src="${escapeAttr(initialSrc)}" alt="" loading="lazy">
        </div>
        <div class="ssl-result__step-content">
          <p class="ssl-result__step-stage">${escapeHtml(step.step || '')}</p>
          <p class="ssl-result__step-name">${escapeHtml(p.name || p.id || '')}</p>
          <p class="ssl-result__step-reason">${escapeHtml(step.reason || '')}</p>
          ${linkHtml}
        </div>
      `;
      el.appendChild(li);
      hydrateShopifyImage(li, p);
    });
  }

  /* Pull the real Shopify product image so the analyzer matches the
   * store's product card. Tries handle candidates in order until one
   * resolves to a 200. Worker may use a different slug than Shopify, so
   * we try url-derived → product.handle → product.id. */
  async function hydrateShopifyImage(li, product) {
    const candidates = handleCandidates(product);
    for (const handle of candidates) {
      try {
        const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
        if (!res.ok) continue;
        const data = await res.json();
        const img = data && (data.featured_image || (data.images && data.images[0]));
        if (!img) continue;
        const fullUrl = img.startsWith('//') ? `https:${img}` : img;
        const slot = li.querySelector('.ssl-result__step-img img');
        if (slot) slot.src = fullUrl;
        return;
      } catch (e) { /* try next candidate */ }
    }
  }
  function handleCandidates(product) {
    if (!product) return [];
    const out = [];
    if (product.url) {
      const m = product.url.match(/\/products\/([^/?#]+)/);
      if (m && m[1]) out.push(m[1]);
    }
    if (product.handle && !out.includes(product.handle)) out.push(product.handle);
    if (product.id && !out.includes(product.id))         out.push(product.id);
    return out;
  }

  /* Worker emits an absolute product URL on the SSL CDN domain. Keep it
   * if present; fall back to a Shopify-style /products/{handle} so links
   * still work if the worker output ever omits the URL. */
  function productUrl(product) {
    if (!product) return '';
    if (product.url) return product.url;
    const handle = product.handle || product.id;
    return handle ? `/products/${handle}` : '';
  }
  function escapeAttr(s) {
    return String(s || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* Worker concern enums (e.g. "tone_unevenness") → readable chip text. */
  function formatConcern(value) {
    if (!value) return '';
    if (typeof value === 'object') return value.label || value.code || '';
    return String(value).replace(/_/g, ' ');
  }

  function humanizeSegment(seg, rec) {
    if (seg.label) return seg.label;
    /* Worker may emit segment as a route id. Map to a friendly label. */
    const SEGMENT_LABELS = {
      sensitive_reactive:    'Sensitive · reactive',
      acne_prone:            'Acne-prone',
      dry_dehydrated:        'Dry · dehydrated',
      oily_congested:        'Oily · congested',
      brightening_even_tone: 'Brightening · even tone',
      anti_aging_firming:    'Anti-aging · firming',
      combination_balance:   'Combination · balance',
      first_timer:           'First-timer',
    };
    const id = seg.id || seg.routeId || (rec && rec.routeId);
    return SEGMENT_LABELS[id] || (id ? String(id).replace(/_/g, ' ') : '');
  }

  /* Phase 2 — Face-mesh overlay.
   * Runs MediaPipe FaceLandmarker on the hero image and paints three
   * staggered layers onto stacked canvases:
   *   - mesh wireframe   (lowest, faintest, longest delay)
   *   - aurora heatmap   (middle, signal-driven blob intensity)
   *   - lab calipers     (top, brightest, no delay)
   *
   * Loads MediaPipe lazily on first call. If detection fails (model load
   * error, no face in photo, very low-resolution), the figure stays at
   * data-mesh="loading" or "fallback" and the static grid + crosshair
   * remain visible — graceful degradation. */
  async function applyFaceMesh(card, img, signals, ext) {
    if (!window.SSLFaceMesh) return;
    const figure = card.querySelector('[data-ssl-hero-photo]');
    if (!figure) return;

    const meshCanvas    = figure.querySelector('[data-ssl-hero-canvas-mesh]');
    const heatmapCanvas = figure.querySelector('[data-ssl-hero-canvas-heatmap]');
    const calipersCanvas = figure.querySelector('[data-ssl-hero-canvas-calipers]');
    if (!meshCanvas || !heatmapCanvas || !calipersCanvas) return;

    figure.dataset.mesh = 'loading';

    /* CRITICAL: renderResult fires the moment the SSE stream emits the
     * recommendation event, but the result-card container stays hidden
     * for ~1.6s while the reveal-state headline plays. If we run canvas
     * sizing now, getBoundingClientRect returns 0×0 (figure is in a
     * `hidden` element) and the drawn paths land on a zero-size buffer.
     * Wait until the figure actually has dimensions before doing anything. */
    await waitForVisible(figure);

    let detection;
    try {
      detection = await window.SSLFaceMesh.detect(img);
    } catch (e) {
      figure.dataset.mesh = 'fallback';
      return;
    }
    if (!detection) {
      figure.dataset.mesh = 'fallback';
      return;
    }

    /* Render all three layers immediately. CSS animations fade them in
     * with their respective delays. */
    try {
      window.SSLFaceMesh.renderMesh(meshCanvas, detection);
      window.SSLFaceMesh.renderHeatmap(heatmapCanvas, detection, signals, ext);
      window.SSLFaceMesh.renderCalipers(calipersCanvas, detection);

      /* Update meta caption with skincare-relevant measurements:
       * SYMMETRY (computed from landmarks) + TONE (light_color_temp from
       * worker quality_check.view_metadata.front when available, falls
       * back to NEUTRAL). Eye-spacing has no skincare bearing — dropped. */
      const cal = window.SSLFaceMesh.calipers(detection.landmarks);
      const m1 = figure.querySelector('[data-ssl-photo-meta-1]');
      const m2 = figure.querySelector('[data-ssl-photo-meta-2]');
      const tone =
        (state.qualityCheck && state.qualityCheck.viewMetadata &&
         state.qualityCheck.viewMetadata.front &&
         state.qualityCheck.viewMetadata.front.light_color_temp) || 'neutral';
      if (m1) m1.textContent = 'SYMMETRY · ' + cal.symmetry;
      if (m2) m2.textContent = 'TONE · ' + String(tone).toUpperCase();

      figure.dataset.mesh = 'done';
    } catch (e) {
      console.warn('[ssl-analyzer] face-mesh render failed', e);
      figure.dataset.mesh = 'fallback';
    }
  }

  /* Resolves once the element has non-zero width AND is in a visible
   * subtree (no `hidden` attribute on any ancestor). Uses ResizeObserver
   * so we react the instant the result-card unhides. Caps at 5s — if
   * the element never appears, give up rather than hang. */
  function waitForVisible(el) {
    return new Promise(resolve => {
      function ready() {
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      }
      if (ready()) return resolve();
      let resolved = false;
      const finish = () => {
        if (resolved) return;
        resolved = true;
        ro.disconnect();
        clearTimeout(timer);
        resolve();
      };
      const ro = new ResizeObserver(() => { if (ready()) finish(); });
      ro.observe(el);
      const timer = setTimeout(finish, 5000);
    });
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }

  // --- Optional analyzer → shade-finder undertone hand-off ------------------

  function persistUndertone(s) {
    const u = s.derivations?.undertone || s.segment?.undertone;
    if (u && ['cool', 'neutral', 'warm'].includes(u)) {
      try { localStorage.setItem('ssl-undertone', u); } catch {}
    }
  }

  document.addEventListener('ssl-analyzer:state', e => {
    if (e.detail.status === 'done') renderResult(e.detail);
    persistUndertone(e.detail);
  });

  // --- Export handlers ------------------------------------------------------

  /* Find the SSL logo URL exposed by the snippet via <meta>. */
  function getLogoUrl() {
    const m = document.querySelector('meta[name="ssl-logo-url"]');
    return m && m.getAttribute('content');
  }

  /* Wrap the result card in an off-screen branded container before snapshot.
   * Returns { wrap, cleanup } so the caller can html2canvas(wrap) then call
   * cleanup() to remove the wrap and put the original card back in place. */
  function buildBrandedWrap(card) {
    const wrap = document.createElement('div');
    wrap.className = 'ssl-export-wrap';
    const logoUrl = getLogoUrl();

    const header = document.createElement('header');
    header.className = 'ssl-export-header';
    header.innerHTML = `
      ${logoUrl ? `<img src="${escapeAttr(logoUrl)}" alt="Summer Skin Lab">` : '<p style="font-family: Lora, Georgia, serif; font-style: italic; font-size: 24px; margin: 0; color: #1a1a1a;">Summer Skin Lab</p>'}
      <p>Skin profile · ${new Date().toLocaleDateString()}</p>
    `;

    const footer = document.createElement('footer');
    footer.className = 'ssl-export-footer';
    footer.innerHTML = `
      <span class="ssl-export-tagline">Skin, returned to.</span>
      <span class="ssl-export-url">summerskinlab.com</span>
    `;

    /* Move the original card into the wrap (preserves listeners + exact
     * computed styles); restore on cleanup. */
    const placeholder = document.createComment('ssl-result-placeholder');
    card.parentNode.insertBefore(placeholder, card);
    wrap.appendChild(header);
    wrap.appendChild(card);
    wrap.appendChild(footer);
    document.body.appendChild(wrap);

    return {
      wrap,
      cleanup: () => {
        placeholder.parentNode.replaceChild(card, placeholder);
        wrap.remove();
      },
    };
  }

  /* Lazy-load the heavy export libs (html2canvas ~199KB + pdf-lib ~525KB)
   * only on first export click. Avoids ~724KB upfront on every analyzer
   * page visit. URLs come from window globals set by the section. */
  let _exportLibsPromise = null;
  function loadExportLibs() {
    if (_exportLibsPromise) return _exportLibsPromise;
    function inject(src) {
      return new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.onload = resolve;
        s.onerror = () => reject(new Error('Failed to load ' + src));
        document.head.appendChild(s);
      });
    }
    const libs = [];
    if (typeof html2canvas !== 'function' && window.SSL_HTML2CANVAS_URL) {
      libs.push(inject(window.SSL_HTML2CANVAS_URL));
    }
    if (typeof PDFLib === 'undefined' && window.SSL_PDF_LIB_URL) {
      libs.push(inject(window.SSL_PDF_LIB_URL));
    }
    _exportLibsPromise = Promise.all(libs);
    return _exportLibsPromise;
  }

  document.addEventListener('click', async (e) => {
    const pngBtn = e.target.closest('[data-ssl-export="png"]');
    if (pngBtn) {
      pngBtn.disabled = true;
      try {
        await loadExportLibs();
      } catch (err) {
        alert('Export libs failed to load — check your connection and try again.');
        pngBtn.disabled = false;
        return;
      }
      if (typeof html2canvas !== 'function') {
        alert('Export not ready — please refresh and try again.');
        pngBtn.disabled = false;
        return;
      }
      const card = document.querySelector('[data-ssl-result-card]');
      if (!card) { pngBtn.disabled = false; return; }
      const branded = buildBrandedWrap(card);
      try {
        const canvas = await html2canvas(branded.wrap, {
          backgroundColor: '#fdfaf2',
          scale: 2,
          useCORS: true,
        });
        const link = document.createElement('a');
        link.download = `summer-skin-lab-profile-${Date.now()}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      } finally {
        branded.cleanup();
        pngBtn.disabled = false;
      }
      return;
    }

    const pdfBtn = e.target.closest('[data-ssl-export="pdf"]');
    if (pdfBtn) {
      pdfBtn.disabled = true;
      try {
        await loadExportLibs();
      } catch (err) {
        alert('Export libs failed to load — check your connection and try again.');
        pdfBtn.disabled = false;
        return;
      }
      if (typeof PDFLib === 'undefined') {
        alert('PDF export not ready — please refresh and try again.');
        pdfBtn.disabled = false;
        return;
      }
      try {
        const s = window.SSLAnalyzer.getState();
        const rec = s.recommendation || {};
        const sig = s.signalsCore || {};
        const response = (rec.response && rec.response.en) || {};
        const { PDFDocument, StandardFonts, rgb } = PDFLib;
        const pdf = await PDFDocument.create();
        const font = await pdf.embedFont(StandardFonts.HelveticaBold);
        const body = await pdf.embedFont(StandardFonts.Helvetica);

        /* Embed the SSL logo on each page. Async fetch + embed; gracefully
         * skip if the logo asset is unreachable. */
        let logoImg = null;
        const logoUrl = getLogoUrl();
        if (logoUrl) {
          try {
            const logoBytes = await fetch(logoUrl).then(r => r.arrayBuffer());
            logoImg = await pdf.embedPng(logoBytes);
          } catch (e) { /* skip */ }
        }

        const charcoal = rgb(0.10, 0.10, 0.10);
        const muted    = rgb(0.42, 0.38, 0.35);
        const gold     = rgb(0.54, 0.43, 0.23);

        function drawHeader(page) {
          if (logoImg) {
            const w = 80;
            const ratio = logoImg.height / logoImg.width;
            page.drawImage(logoImg, { x: 60, y: 752, width: w, height: w * ratio });
          } else {
            page.drawText('SUMMER SKIN LAB', { x: 60, y: 760, size: 11, font, color: charcoal });
          }
          page.drawText('SKIN PROFILE', { x: 432, y: 760, size: 9, font, color: muted });
        }
        function drawFooter(page, pageNum, total) {
          page.drawText('summerskinlab.com', { x: 60, y: 36, size: 9, font, color: gold });
          page.drawText(`${pageNum} / ${total}`, { x: 540, y: 36, size: 9, font: body, color: muted });
        }

        /* ── Cover ── */
        const cover = pdf.addPage([612, 792]);
        drawHeader(cover);
        cover.drawText(rec.headline || 'Your skin profile', {
          x: 60, y: 540, size: 32, font, color: charcoal,
        });
        if (sig.summary) {
          drawWrappedText(cover, sig.summary, 60, 480, 492, body, 12, muted);
        }
        cover.drawText(new Date().toLocaleDateString(), { x: 60, y: 100, size: 10, font: body, color: muted });
        if (s.sessionId) {
          cover.drawText('Session · ' + String(s.sessionId).slice(0, 8), { x: 60, y: 84, size: 9, font: body, color: muted });
        }
        drawFooter(cover, 1, 3);

        /* ── Page 2 — concerns + strengths + signals ── */
        const p2 = pdf.addPage([612, 792]);
        drawHeader(p2);
        p2.drawText('What we saw', { x: 60, y: 700, size: 18, font, color: charcoal });
        let y = 670;
        (sig.topConcerns || []).forEach(c => {
          p2.drawText('·  ' + String(c).replace(/_/g, ' '), { x: 60, y, size: 12, font: body, color: charcoal });
          y -= 18;
        });
        if (sig.strengths && sig.strengths.length) {
          y -= 16;
          p2.drawText("What's working", { x: 60, y, size: 18, font, color: charcoal });
          y -= 26;
          sig.strengths.forEach(strength => {
            p2.drawText('·  ' + strength, { x: 60, y, size: 12, font: body, color: gold });
            y -= 18;
          });
        }
        const signalScores = sig.signals || {};
        const SIG_LABELS = {
          redness_score: 'Redness', texture_score: 'Skin texture',
          dryness_score: 'Dryness', tone_unevenness_score: 'Uneven tone',
          undereye_score: 'Under-eye darkness', pore_visibility_score: 'Pore visibility',
          oiliness_score: 'Oiliness', blemish_score: 'Blemishes',
        };
        y -= 24;
        p2.drawText('Skin signals', { x: 60, y, size: 18, font, color: charcoal });
        y -= 26;
        Object.keys(SIG_LABELS).forEach(k => {
          const v = signalScores[k] || 0;
          p2.drawText(SIG_LABELS[k], { x: 60, y, size: 11, font: body, color: charcoal });
          p2.drawText(v + '%', { x: 240, y, size: 11, font, color: charcoal });
          /* Bar */
          p2.drawRectangle({ x: 280, y: y + 2, width: 240, height: 4, color: rgb(0.92, 0.90, 0.86) });
          p2.drawRectangle({ x: 280, y: y + 2, width: 240 * (v / 100), height: 4, color: gold });
          y -= 18;
        });
        drawFooter(p2, 2, 3);

        /* ── Page 3 — routine + climate ── */
        const p3 = pdf.addPage([612, 792]);
        drawHeader(p3);
        p3.drawText('Your daily ritual', { x: 60, y: 700, size: 18, font, color: charcoal });
        y = 670;
        function drawRoutineColumn(eyebrow, steps, x) {
          p3.drawText(eyebrow.toUpperCase(), { x, y, size: 9, font, color: gold });
          let yy = y - 18;
          (steps || []).forEach((step, idx) => {
            if (!step || !step.product) return;
            const num = String(idx + 1).padStart(2, '0');
            p3.drawText(num + ' · ' + (step.step || '').toUpperCase(),
              { x, y: yy, size: 8.5, font, color: muted });
            yy -= 12;
            const name = step.product.name || step.product.id || '';
            yy = drawWrappedText(p3, name, x, yy, 230, font, 10.5, charcoal) - 4;
            yy = drawWrappedText(p3, step.reason || '', x, yy, 230, body, 9, muted) - 12;
          });
        }
        drawRoutineColumn('Morning', rec.morning, 60);
        drawRoutineColumn('Night',   rec.night,   316);
        if (rec.weeklyAddOn) {
          y = 200;
          p3.drawText('WEEKLY · AFTER 2 WEEKS', { x: 60, y, size: 9, font, color: gold });
          y -= 14;
          y = drawWrappedText(p3, rec.weeklyAddOn.product.name || '', 60, y, 492, font, 11, charcoal) - 4;
          y = drawWrappedText(p3, rec.weeklyAddOn.reason || '', 60, y, 492, body, 9, muted);
        }
        if (response.climateNote) {
          p3.drawText('FOR PHNOM PENH CLIMATE', { x: 60, y: 110, size: 9, font, color: gold });
          drawWrappedText(p3, response.climateNote, 60, 96, 492, body, 9.5, muted);
        }
        drawFooter(p3, 3, 3);

        const bytes = await pdf.save();
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `summer-skin-lab-profile-${Date.now()}.pdf`;
        link.click();
      } finally {
        pdfBtn.disabled = false;
      }
    }
  });

  /* Word-wrap helper for the PDF. Returns the y-coordinate of the line
   * after the last drawn line so callers can chain blocks without manual
   * height math. */
  function drawWrappedText(page, text, x, y, maxWidth, font, size, color) {
    if (!text) return y;
    const words = String(text).split(/\s+/);
    const lines = [];
    let line = '';
    words.forEach(w => {
      const trial = line ? line + ' ' + w : w;
      if (font.widthOfTextAtSize(trial, size) > maxWidth) {
        lines.push(line);
        line = w;
      } else {
        line = trial;
      }
    });
    if (line) lines.push(line);
    lines.forEach((ln, i) => {
      page.drawText(ln, { x, y: y - i * (size + 2), size, font, color });
    });
    return y - lines.length * (size + 2);
  }

  // Public API
  window.SSLAnalyzer = {
    setPhoto(slot, dataUrl) { set({ photos: { ...state.photos, [slot]: dataUrl } }); },
    getState() { return structuredClone(state); },
    reset() { state = structuredClone(initial); document.dispatchEvent(new CustomEvent('ssl-analyzer:state', { detail: structuredClone(state) })); },
    streamScan,
  };
})();
